// ─────────────────────────────────────────────────────────────────────────────
// BRANDING — edit this file to change the console's colours, fonts, logos and
// login-page copy.
//
// Colours come in two palettes, light and dark. applyBrand() publishes them as
// CSS custom properties (colors.light.primary → var(--brand-primary)) under
// :root[data-theme="light"] and :root[data-theme="dark"]. The theme toggle
// flips data-theme on <html>, so stylesheets never hard-code a colour and never
// need to know which theme is active.
//
// Palette source: the xenscope portal (../xenscope/99-portal/tailwind.config.js)
// and the xenscope logo (lime leaf over teal leaf, navy wordmark). Actions use
// xenscope navy #202937; the teal is kept for the logo and the product icons.
// ─────────────────────────────────────────────────────────────────────────────

// Every palette must define the same keys (TypeScript enforces this below)
type Palette = {
  primary: string; // main actions, active nav, focus
  primaryHover: string;
  primaryText: string; // text on primary
  accent: string; // lime highlights, accent bars, call-to-action
  accentHover: string;
  accentText: string; // text on accent
  accentBg: string; // tinted background for the active nav row
  edge: string; // coloured card edges (e.g. KPI tile top border)
  heading: string; // page titles
  link: string; // inline links and text buttons
  forest: string; // secondary palette from xenscope.com
  amber: string;
  sage: string;
  background: string; // page background
  surface: string; // cards, top bar, auth card
  surfaceRaised: string; // chips and hover fills on top of surfaces
  text: string;
  textSecondary: string;
  textTertiary: string;
  border: string; // hairlines and card borders
  borderStrong: string; // input outlines
  success: string;
  successBg: string;
  danger: string;
  dangerBg: string;
  shadow: string; // card drop shadow colour
  // Product colours: the icon tile for each kind of component, everywhere it appears
  ashpBg: string; // heat pump (ASHP Estimator): teal for now (was blue #1F5FBF on #E3EEFF)
  ashp: string;
  solarBg: string; // solar estimate: teal for now (was amber #B45309 on #FFF0D4)
  solar: string;
  contactBg: string; // contact form: teal (the brand teal, #014852)
  contact: string;
};

const light: Palette = {
  primary: "#202937", // xenscope navy (dark blue)
  primaryHover: "#141B26",
  primaryText: "#FFFFFF",
  accent: "#E1EE2B", // xenscope lime
  accentHover: "#CFDC1C",
  accentText: "#202937",
  accentBg: "#E8EBF0", // light navy tint for the active nav row
  edge: "#202937", // navy: lime is too faint as a thin border on white
  heading: "#202937", // xenscope navy: app.xenscope.com text-xtext, the logo wordmark
  link: "#202937",
  forest: "#3B534A",
  amber: "#FBB03B",
  sage: "#7F985B",
  background: "#EEEEEE",
  surface: "#FFFFFF",
  surfaceRaised: "#F6F6F5",
  text: "#202937", // slate, as in the logo wordmark
  textSecondary: "#5F6670",
  textTertiary: "#8E959E",
  border: "#E5E4E2",
  borderStrong: "#9A9A9A",
  success: "#15803D",
  successBg: "#ECFDF3",
  danger: "#C0283F",
  dangerBg: "#FDECEF",
  shadow: "rgba(32, 41, 55, 0.08)",
  ashpBg: "#DCEFF0",
  ashp: "#014852",
  solarBg: "#DCEFF0",
  solar: "#014852",
  contactBg: "#DCEFF0",
  contact: "#014852",
};

// Dark: deep navy surfaces derived from xenscope navy, with the lime accent
// kept as-is and a lifted blue for anything that must read on dark.
const dark: Palette = {
  primary: "#3F5B85",
  primaryHover: "#4B6A98",
  primaryText: "#FFFFFF",
  accent: "#E1EE2B",
  accentHover: "#EEF76A",
  accentText: "#0F1729",
  accentBg: "#1D2B3F",
  edge: "#E1EE2B", // lime reads well on dark surfaces
  heading: "#DDE4EE", // pale navy tint of #202937, readable on dark
  link: "#B9C7DB",
  forest: "#6F9A8A",
  amber: "#FBB03B",
  sage: "#A4BE7E",
  background: "#0F1729",
  surface: "#172133",
  surfaceRaised: "#1F2B40",
  text: "#E6EAF0",
  textSecondary: "#A7B1C2",
  textTertiary: "#737F95",
  border: "#27334A",
  borderStrong: "#3E4B66",
  success: "#4ADE80",
  successBg: "#0F2E1E",
  danger: "#F2667B",
  dangerBg: "#3A1520",
  shadow: "rgba(0, 0, 0, 0.35)",
  ashpBg: "#0B3338",
  ashp: "#7CC9CF",
  solarBg: "#0B3338",
  solar: "#7CC9CF",
  contactBg: "#0B3338",
  contact: "#7CC9CF",
};

export const brand = {
  // Product naming. The console is xenquote; xenscope is the "powered by" credit.
  productName: "xenquote",
  productSubtitle: "Digital sales tools for renewables",
  poweredBy: "Powered by xenscope",
  legalName: "Synvu Limited",

  // Logos live in console/public/brand/ (copied from the xenscope portal assets)
  logo: {
    icon: "/brand/xenscope-icon-no-text.webp", // two-leaf mark, works on both themes
    wordmarkDark: "/brand/logo-blue-text.webp", // slate "xenscope", for light theme
    wordmarkLight: "/brand/logo-white-text.webp", // white "xenscope", for dark theme
  },

  // Typefaces: Manrope (shared with the marketing site — a clean geometric sans;
  // see docs/09), with JetBrains Mono for codes and IDs. The Google Fonts <link>
  // in index.html must load the same families.
  font: {
    family: '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
  },

  colors: { light, dark },

  // Radius family aligned with the marketing site (site: xs 8 / sm 12 / md 16);
  // the console stays a touch denser but shares the softer, consistent shape.
  radius: {
    sm: "8px",
    md: "12px",
    lg: "16px",
  },

  // Login page left-rail copy
  login: {
    // xenquote is the toolkit that fills a renewables installer's gaps online, all of it leading to leads
    headline: "Your front door online.\nBuilt for renewables companies.",
    tagline:
      "Instant heat pump and solar estimates on your website, a hosted page ready to share, and contact forms that catch every enquiry, with every lead listed and mailshots to follow up. Upgrade to QuoteFlow for quotes your customers accept online.\nThe online presence a renewables business needs, in one place.",
    termsUrl: "https://xenquote.com/terms/",
    privacyUrl: "https://xenquote.com/privacy/",
  },
} as const;

// camelCase key → kebab-case CSS custom property name
function toCssVar(prefix: string, key: string): string {
  return `--${prefix}-${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;
}

function declarations(prefix: string, values: Record<string, string>): string {
  return Object.entries(values)
    .map(([k, v]) => `${toCssVar(prefix, k)}: ${v};`)
    .join(" ");
}

/**
 * Publish brand tokens as CSS custom properties, one block per theme, in a
 * <style> tag. Call once before render; the theme itself is chosen by the
 * data-theme attribute (set by the bootstrap script in index.html).
 */
export function applyBrand(): void {
  const shared = `${declarations("radius", brand.radius)} --font-sans: ${brand.font.family}; --font-mono: ${brand.font.mono};`;
  const css = [
    `:root { ${shared} }`,
    // Light is also the fallback if data-theme is somehow missing
    `:root, :root[data-theme="light"] { ${declarations("brand", brand.colors.light)} color-scheme: light; }`,
    `:root[data-theme="dark"] { ${declarations("brand", brand.colors.dark)} color-scheme: dark; }`,
  ].join("\n");

  let tag = document.getElementById("brand-tokens");
  if (!tag) {
    tag = document.createElement("style");
    tag.id = "brand-tokens";
    document.head.appendChild(tag);
  }
  tag.textContent = css;
  document.title = `${brand.productName} · ${brand.productSubtitle}`;
}
